import {
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  DropDown,
  Flex,
  FlexColumn,
  FlexSpace,
  FormControls,
  LoadingButton,
} from '@elementary/common-ui/components';
import {
  OwnersChipList,
  TagsChipList,
} from '@elementary/common-ui/components/ChipList';
import {
  AnomalyDetectionChart,
  AutomatedTestConfigurationApi,
  FreshnessAnomalyDetectionChart,
  Period,
  SensitivityEstimation,
} from '@elementary/common-ui/types';
import Typography from '@mui/material/Typography';
import { Controller, UseFormReturn, useForm } from 'react-hook-form';
import { AutomatedTestSeverities } from 'shared/constants';
import { SimulateTestResultApi, VolumeDetectionMode } from 'shared/types';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import { space } from '@elementary/common-ui/style/utils';
import { PipelineTaskPerformanceTestConfig } from '../../types';
import CloudFreshnessFields from './CloudFreshnessFields';
import CloudVolumeFields from './CloudVolumeFields';
import PipelineTaskPerformanceTestConfigurationFields from './PipelineTaskPerformanceTestConfigurationFields';
import { getInitialVolumeMode } from './utils';

const defaultEstimations: SensitivityEstimation[] = [
  { sensitivity: 'low' },
  { sensitivity: 'medium' },
  { sensitivity: 'high' },
];

type AccordionSummaryProps = {
  title: ReactNode;
  onSave: () => void;
  isLoading: boolean;
  isExpanded: boolean;
  readOnly?: boolean;
  saveDisabled?: boolean;
};

const AccordionSummary = ({
  title,
  onSave,
  isLoading,
  isExpanded,
  readOnly,
  saveDisabled,
}: AccordionSummaryProps) => (
  <FlexSpace sx={{ width: '100%', height: '24px' }}>
    <Typography sx={{ lineHeight: 0 }}>{title}</Typography>
    {isExpanded && !readOnly && (
      <LoadingButton
        sx={{ width: 'fit-content', margin: space(0, 0, 0, 'auto') }}
        loading={isLoading}
        disabled={saveDisabled}
        onClick={(e) => {
          onSave();
          e.stopPropagation();
        }}
      >
        Save changes
      </LoadingButton>
    )}
  </FlexSpace>
);

type ConfigFormValues =
  | AutomatedTestConfigurationApi
  | PipelineTaskPerformanceTestConfig;

type Props = {
  anomalyConfig: ConfigFormValues;
  anomalyChart: AnomalyDetectionChart | FreshnessAnomalyDetectionChart | null;
  onSimulate: (values: SimulateTestResultApi) => void;
  isLoading: boolean;
  isSimulating: boolean;
  tagsOptions?: string[];
  ownersOptions?: string[];
  estimatedSensitivityRange?: SensitivityEstimation[];
  disabledSimulator?: boolean;
  onSave?: () => void;
  readOnly?: boolean;
  savedTrainingPeriod?: Period;
};

const AnomalyTestConfigurationFields = forwardRef(
  (
    {
      anomalyConfig,
      anomalyChart,
      onSimulate,
      isLoading,
      isSimulating,
      tagsOptions,
      ownersOptions,
      estimatedSensitivityRange = defaultEstimations,
      disabledSimulator,
      onSave,
      readOnly,
      savedTrainingPeriod,
    }: Props,
    ref,
  ) => {
    const methods = useForm<ConfigFormValues>({
      mode: 'onChange',
      defaultValues: anomalyConfig,
    });

    const [testSettingsExpanded, setTestSettingsExpanded] = useState(true);
    const [testMetadataExpanded, setTestMetadataExpanded] = useState(false);
    const [volumeMode, setVolumeMode] = useState<VolumeDetectionMode>(() =>
      getInitialVolumeMode(anomalyConfig as AutomatedTestConfigurationApi),
    );

    useImperativeHandle(ref, () => ({
      methods,
      volumeMode,
    }));

    const { onPanelConfigChanged } = useSidePanelParams();

    // This is for blocking the user from closing the side panel if he has unsaved changes..
    // If the config is changed, updating the URL param appropriately through the hook.
    useEffect(() => {
      onPanelConfigChanged(methods.formState.isDirty);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [methods.formState.isDirty]);

    return (
      <FlexColumn sx={{ gap: 3, overflowY: 'auto' }}>
        <FormControls.TestAccordion
          summary={
            <AccordionSummary
              title="Test Settings"
              isLoading={isLoading}
              onSave={() => onSave?.()}
              isExpanded={testSettingsExpanded}
              readOnly={readOnly}
              saveDisabled={!methods.formState.isValid}
            />
          }
          expanded={testSettingsExpanded}
          onChange={(_, expanded) => setTestSettingsExpanded(expanded)}
        >
          <FlexColumn sx={{ gap: 3, p: 3 }}>
            {anomalyConfig.type === 'anomaly_detection' && (
              <CloudVolumeFields
                chartData={anomalyChart as AnomalyDetectionChart}
                methods={
                  methods as UseFormReturn<AutomatedTestConfigurationApi>
                }
                onSimulate={(values) =>
                  onSimulate({ type: 'anomaly_detection', config: values })
                }
                isLoading={isSimulating}
                disabledSimulator={disabledSimulator}
                readOnly={readOnly}
                savedTrainingPeriod={
                  savedTrainingPeriod ??
                  anomalyConfig.anomaly_config.training_period
                }
                mode={volumeMode}
                onModeChange={setVolumeMode}
              />
            )}

            {anomalyConfig.type === 'freshness_anomaly_detection' && (
              <CloudFreshnessFields
                chartData={anomalyChart as FreshnessAnomalyDetectionChart}
                methods={
                  methods as UseFormReturn<AutomatedTestConfigurationApi>
                }
                onSimulate={(values) =>
                  onSimulate({
                    type: 'freshness_anomaly_detection',
                    config: values,
                  })
                }
                isLoading={isSimulating}
                estimatedSensitivityRange={estimatedSensitivityRange}
                readOnly={readOnly}
                savedTrainingPeriod={
                  savedTrainingPeriod ??
                  anomalyConfig.anomaly_config.training_period
                }
              />
            )}

            {anomalyConfig.type === 'pipeline_task_performance' && (
              <PipelineTaskPerformanceTestConfigurationFields
                methods={
                  methods as UseFormReturn<PipelineTaskPerformanceTestConfig>
                }
                readOnly={readOnly}
                onSimulate={onSimulate}
                isSimulating={isSimulating}
                chartData={anomalyChart as AnomalyDetectionChart | null}
              />
            )}

            <FormControls.FieldsWrapper>
              <FormControls.Row>
                <FormControls.FormText>Severity: </FormControls.FormText>
                <Controller
                  control={methods.control}
                  name="severity"
                  render={({ field }) => (
                    <DropDown
                      size="large"
                      disabled={readOnly}
                      sx={{ minWidth: '120px' }}
                      options={[
                        {
                          value: String(AutomatedTestSeverities.ERROR),
                          label: 'Error',
                        },
                        {
                          value: String(AutomatedTestSeverities.WARN),
                          label: 'Warning',
                        },
                      ]}
                      value={String(field.value)}
                      onChange={(value) => field.onChange(Number(value))}
                    />
                  )}
                />
              </FormControls.Row>
            </FormControls.FieldsWrapper>
          </FlexColumn>
        </FormControls.TestAccordion>

        <FormControls.TestAccordion
          summary={
            <AccordionSummary
              title={
                <Flex>
                  <Typography>Test Metadata</Typography>
                  <Typography
                    sx={{ color: 'content.secondary' }}
                  >{`\xa0(optional)`}</Typography>
                </Flex>
              }
              isLoading={isLoading}
              onSave={() => onSave?.()}
              isExpanded={testMetadataExpanded}
              readOnly={readOnly}
              saveDisabled={!methods.formState.isValid}
            />
          }
          expanded={testMetadataExpanded}
          onChange={(_, expanded) => setTestMetadataExpanded(expanded)}
        >
          <FlexColumn sx={{ gap: 3, p: 3 }}>
            <FormControls.FieldsWrapper>
              <FormControls.Row>
                <FormControls.FormText>Owners:</FormControls.FormText>
                <Controller
                  name="owners"
                  control={methods.control}
                  render={({ field }) => (
                    <OwnersChipList
                      {...field}
                      buttonText="Add owner"
                      autoCompleteOptions={ownersOptions}
                      readOnly={readOnly}
                    />
                  )}
                />
              </FormControls.Row>

              <FormControls.Row>
                <FormControls.FormText>Tags:</FormControls.FormText>
                <Controller
                  name="tags"
                  control={methods.control}
                  render={({ field }) => (
                    <TagsChipList
                      {...field}
                      buttonText="Add tag"
                      autoCompleteOptions={tagsOptions}
                      readOnly={readOnly}
                    />
                  )}
                />
              </FormControls.Row>
            </FormControls.FieldsWrapper>
          </FlexColumn>
        </FormControls.TestAccordion>
      </FlexColumn>
    );
  },
);

export default AnomalyTestConfigurationFields;
